import type { CustomRendererProps } from 'streamdown'
import { useSyncExternalStore } from 'react'
import { Compass } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useAppTabs } from '@/hooks/useAppTabs'
import { chameleon } from '@/lib/chameleon'

export type TourFence = {
  /** Chameleon experience id, copied verbatim from David's catalogue. */
  id: string
  /**
   * Route the tour's first step is anchored to. Absent when the tour targets
   * chrome that is mounted everywhere, which is what makes it launchable from
   * wherever the user already is.
   */
  route?: string
}

/** The launchable half of a hoku/agents/david/tours.json row. */
export type TourRow = { id: string; route: string | null }

/**
 * Mirrors hoku/agents/david/tours.json, the file David's prompt is rendered
 * from. Copied rather than imported because ui/ is its own Docker build context
 * (the frontend service in docker-compose), so the bundle cannot reach outside
 * it — same arrangement as features/duties/authConstants.ts. The drift guard in
 * this component's test is what keeps the two honest.
 */
export const CATALOGUE: TourRow[] = [
  { id: '6a68ef50b46506002bac2d95', route: null },
]

const FIELD = /^(id|route):\s*(\S+)$/

/**
 * Parses a ```tour fence body — grammar only; `resolveTour` is what decides the
 * block names a tour that exists.
 *
 * Strict on purpose: any line it does not recognize returns null and no control
 * renders. Guessing at a malformed block risks navigating somewhere unintended.
 */
export function parseTourFence(code: string): TourFence | null {
  const fields = new Map<string, string>()
  for (const line of code.trim().split('\n')) {
    const field = FIELD.exec(line.trim())
    if (!field) return null
    fields.set(field[1]!, field[2]!)
  }

  const id = fields.get('id')
  const route = fields.get('route')
  if (!id) return null
  if (route !== undefined && !route.startsWith('/')) return null

  return route ? { id, route } : { id }
}

/**
 * Resolves a fence to the catalogue row it names. Model output is not an
 * enforcement boundary — David can invent a syntactically valid id, or be talked
 * into one — and an invented id renders a control that starts nothing while an
 * invented route drops the user on an unrelated page. So only a documented id
 * resolves, and both values then come from that row rather than from the fence.
 */
export function resolveTour(
  code: string,
  catalogue: TourRow[] = CATALOGUE
): TourFence | null {
  const fence = parseTourFence(code)
  if (!fence) return null

  // Matched on id alone: the route is taken from the row either way, so also
  // matching on `fence.route` would buy no safety while letting the model's
  // least reliable output withhold the control — a trailing slash or an omitted
  // line on a perfectly correct id would resolve to nothing.
  const row = catalogue.find((candidate) => candidate.id === fence.id)
  if (!row) return null

  return row.route ? { id: row.id, route: row.route } : { id: row.id }
}

/**
 * Streamdown custom renderer for ```tour fences.
 *
 * One rule: the control renders only when clicking it would actually start
 * something. A fence still streaming, a block that does not parse or names no
 * catalogued tour, and a Chameleon that never loaded (adblocker, offline, dev)
 * all render nothing — a button that silently does nothing is worse than no
 * button.
 */
export function TourRenderer({
  code,
  isIncomplete,
  catalogue,
}: CustomRendererProps & { catalogue?: TourRow[] }) {
  const { openTab } = useAppTabs()
  // Subscribed rather than read once: readiness now means a CDN fetch landed,
  // which can happen after the answer finished streaming. MessageResponse is
  // memoized on `children`, so a finished message never re-renders on its own —
  // without this the control would stay missing on a message that offered it.
  const ready = useSyncExternalStore(
    chameleon.subscribeReady,
    chameleon.isReady
  )

  const tour = isIncomplete ? null : resolveTour(code, catalogue)
  if (!tour || !ready) return null

  const start = () => {
    // Navigate first — the tour's first step has nothing to anchor to until the
    // route it targets is mounted. Chameleon then waits for that anchor itself,
    // which is why triggers are honored only on this path.
    if (tour.route) openTab(tour.route)
    chameleon.show(tour.id, { honorTriggers: Boolean(tour.route) })
  }

  return (
    <Button variant="outline" size="sm" onClick={start}>
      <Compass />
      Show me how
    </Button>
  )
}
